import React, { useState } from "react";
import Box from "@mui/material/Box";
import TextField from "@mui/material/TextField";
import Grid from "@mui/material/Grid";
import { useNavigate, useLocation, Link } from "react-router-dom";
import useMediaQuery from "@mui/material/useMediaQuery";
import { Button, IconButton, InputAdornment, Typography } from "@mui/material";
import { useAppSelector, useAppDispatch } from "../../../app/hooks";
import AppButton from "../../common/AppButton";
import LoadingSpinner from "../../common/Loading";
import ProfileUploadIcon from "../../IconComp/profileUploadIcon";
import { DatePicker, LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";

interface IProps {
  value: any;
  errors: any;
  handleInputChange: any;
  handleImageChange: any;
  fileName: any;
  handleContinuePass: any;
  termsAccepted: any;
  setTermsAccepted: any;
}

const SignUpDetail = ({
  value,
  errors,
  handleInputChange,
  handleImageChange,
  fileName,
  handleContinuePass,
  termsAccepted,
  setTermsAccepted,
}: IProps) => {
  const navigate = useNavigate();
  const { state }: any = useLocation();
  const { isLoading, isLoginError, loginMessage } = useAppSelector(
    (state: any) => state?.auth
  );
  const dispatch = useAppDispatch();
  const isMobile = useMediaQuery("(max-width: 1022px)");
  const [termsError, setTermsError] = useState<any>("");

  const handleCheckboxChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setTermsAccepted(event.target.checked);

    // Clear error when checkbox is checked
    if (event.target.checked) {
      setTermsError(""); // Remove terms error when accepted
    }
  };
  const handleContinue = () => {
    handleContinuePass(); // Call the function first
    if (!termsAccepted) {
      // Optionally show a message or alert here
      setTermsError("You must accept the terms and conditions to continue.");
    } else {
      setTermsError(""); // Clear error if terms accepted
      //handleContinuePass(); // Proceed to next step
    }
  };

  return (
    <>
      <Grid
        container
        spacing={2}
        style={{
          paddingLeft: isMobile ? "0px" : "30px",
          paddingRight: isMobile ? "0px" : "30px",
        }}
      >
        <Grid item xs={12}>
          <TextField
            label="Pharmacy Name"
            name="name"
            value={value.name}
            onChange={handleInputChange}
            error={Boolean(errors.name)}
            helperText={errors.name}
            fullWidth
            sx={{
              "& .MuiInputBase-root": {
                height: "40px", // Set the total height of the input field
                padding: "0", // Remove default padding
                display: "flex",
                alignItems: "center", // Center the input content
                justifyContent: "center",
              },
              "& .MuiFormLabel-root": {
                top: "-5px", // Adjust label positioning if necessary
              },
              "& .MuiInputBase-input": {
                padding: "0 10px", // Add inner padding for input
                height: "100%", // Fill the container's height
                boxSizing: "border-box",
              },
            }}
          />
        </Grid>
        <Grid item xs={12}>
          <TextField
            label="Pharmacy Registration No."
            name="pharmacy_reg"
            value={value.pharmacy_reg}
            onChange={handleInputChange}
            error={Boolean(errors.pharmacy_reg)}
            helperText={errors.pharmacy_reg}
            fullWidth
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <Typography
                    sx={{
                      fontSize: "12px",
                      color: "#888",
                      whiteSpace: "nowrap", // Prevents line breaks
                      padding: "5px", // Adds spacing from input text
                    }}
                  >
                    (Optional)
                  </Typography>
                </InputAdornment>
              ),
            }}
            sx={{
              "& .MuiInputBase-root": {
                height: "40px", // Set the total height of the input field
                padding: "0", // Remove default padding
                display: "flex",
                alignItems: "center", // Center the input content
                justifyContent: "center",
              },
              "& .MuiFormLabel-root": {
                top: "-5px", // Adjust label positioning if necessary
              },
              "& .MuiInputBase-input": {
                padding: "0 10px", // Add inner padding for input
                height: "100%", // Fill the container's height
                boxSizing: "border-box",
              },
            }}
          />
        </Grid>
        <Grid item xs={12}>
          <TextField
            label="License Number"
            name="license_number"
            value={value?.license_number}
            onChange={handleInputChange}
            error={Boolean(errors.license_number)}
            helperText={errors.license_number}
            fullWidth
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <Typography
                    sx={{
                      fontSize: "12px",
                      color: "#888",
                      whiteSpace: "nowrap", // Prevents line breaks
                      padding: "5px", // Adds spacing from input text
                    }}
                  >
                    (Optional)
                  </Typography>
                </InputAdornment>
              ),
            }}
            sx={{
              "& .MuiInputBase-root": {
                height: "40px", // Set the total height of the input field
                padding: "0", // Remove default padding
                display: "flex",
                alignItems: "center", // Center the input content
                justifyContent: "center",
              },
              "& .MuiFormLabel-root": {
                top: "-5px", // Adjust label positioning if necessary
              },
              "& .MuiInputBase-input": {
                padding: "0 10px", // Add inner padding for input
                height: "100%", // Fill the container's height
                boxSizing: "border-box",
              },
            }}
          />
        </Grid>

        <Grid item xs={12}>
          <LocalizationProvider dateAdapter={AdapterDateFns}>
            <DatePicker
              label="License Expiry Date"
              value={value?.license_expire_date}
              onChange={(newValue: any) => {
                // Ensure the new value is a valid Date or null
                if (newValue instanceof Date && !isNaN(newValue.getTime())) {
                  handleInputChange({
                    target: {
                      name: "license_expire_date",
                      value: newValue.toISOString(), // Convert to ISO string
                    },
                  });
                } else {
                  handleInputChange({
                    target: {
                      name: "license_expire_date",
                      value: null, // Set to null if invalid
                    },
                  });
                }
              }}
              renderInput={(params: any) => (
                <TextField
                  {...params}
                  error={Boolean(errors.license_expire_date)}
                  helperText={errors.license_expire_date}
                  fullWidth
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        <Box
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            gap: "5px",
                          }}
                        >
                          {params.InputProps?.endAdornment}
                          <Typography
                            sx={{
                              fontSize: "12px",
                              color: "#888",
                              whiteSpace: "nowrap",
                              marginRight: "-5px",
                            }}
                          >
                            (Optional)
                          </Typography>
                        </Box>
                      </InputAdornment>
                    ),
                  }}
                  sx={{
                    "& .MuiInputBase-root": {
                      height: "40px",
                      padding: "0",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                    },
                    "& .MuiFormLabel-root": {
                      top: "-5px",
                    },
                    "& .MuiInputBase-input": {
                      padding: "0 10px",
                      height: "100%",
                      boxSizing: "border-box",
                    },
                    "& .MuiInputAdornment-root": {
                      marginRight: "10px", // Prevent the icon from being pushed out
                    },
                  }}
                />
              )}
              inputFormat="dd/MM/yyyy" // Customize the format if needed
              openTo="day" // Start with the day selection
              views={["day", "month", "year"]}
            />
          </LocalizationProvider>
        </Grid>

        <Grid item xs={12}>
          <TextField
            label="Customer Service Number"
            name="contact_number"
            value={value.contact_number}
            onChange={handleInputChange}
            error={Boolean(errors.contact_number)}
            helperText={errors.contact_number}
            fullWidth
            sx={{
              "& .MuiInputBase-root": {
                height: "40px", // Set the total height of the input field
                padding: "0", // Remove default padding
                display: "flex",
                alignItems: "center", // Center the input content
                justifyContent: "center",
              },
              "& .MuiFormLabel-root": {
                top: "-5px", // Adjust label positioning if necessary
              },
              "& .MuiInputBase-input": {
                padding: "0 10px", // Add inner padding for input
                height: "100%", // Fill the container's height
                boxSizing: "border-box",
              },
            }}
          />
        </Grid>
        <Grid item xs={12}>
          <TextField
            label="Operating Hours"
            name="operating_hours"
            value={value?.operating_hours}
            onChange={handleInputChange}
            error={Boolean(errors.operating_hours)}
            helperText={errors.operating_hours}
            fullWidth
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <Typography
                    sx={{
                      fontSize: "12px",
                      color: "#888",
                      whiteSpace: "nowrap", // Prevents line breaks
                      padding: "5px", // Adds spacing from input text
                    }}
                  >
                    (Optional)
                  </Typography>
                </InputAdornment>
              ),
            }}
            sx={{
              "& .MuiInputBase-root": {
                height: "40px", // Set the total height of the input field
                padding: "0", // Remove default padding
                display: "flex",
                alignItems: "center", // Center the input content
                justifyContent: "center",
              },
              "& .MuiFormLabel-root": {
                top: "-5px", // Adjust label positioning if necessary
              },
              "& .MuiInputBase-input": {
                padding: "0 10px", // Add inner padding for input
                height: "100%", // Fill the container's height
                boxSizing: "border-box",
              },
            }}
          />
        </Grid>
        <Grid item xs={12} style={{ textAlign: "center" }}>
          <label
            htmlFor="upload-file"
            style={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              gap: "10px",
              cursor: "pointer",
            }}
          >
            <input
              type="file"
              id="upload-file"
              onChange={handleImageChange}
              style={{ display: "none" }}
            />
            <Button
              component="span"
              fullWidth
              style={{
                backgroundColor: "#E7ECFC",
                color: "#0B0B0B",
                padding: "10px 20px",
                borderRadius: "5px",
                border: "1px solid #1849D6",
                textTransform: "none",
                height: "100px",
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <Box sx={{ display: "flex", flexDirection: "row", gap: 1 }}>
                <Box sx={{ mt: "3px" }}>
                  <ProfileUploadIcon />
                </Box>
                <Box sx={{ fontSize: "18px", fontWeight: 400 }}>
                  {/* Upload Profile logo */}
                  {fileName ? fileName : "Upload Profile"}
                </Box>
              </Box>
              <Box
                sx={{
                  color: "#6D6D6D",
                  display: "block",
                  fontSize: "10px",
                  fontWeight: 400,
                }}
              >
                Supports PNG,JPEG,JPG
              </Box>
              {/* Optional Text */}
              <Box
                sx={{
                  fontSize: "12px",
                  color: "#888",
                  position: "absolute",
                  right: "5px",
                  bottom: "5px",
                }}
              >
                (Optional)
              </Box>
            </Button>
          </label>
        </Grid>
        {errors.logo && (
          <p
            style={{
              color: "#CB2C2C",
              fontSize: "12px",
              marginLeft: "15px",
            }}
          >
            {errors.logo}
          </p>
        )}
        <Grid item xs={12}>
          <div>
            <input
              type="checkbox"
              checked={termsAccepted}
              onChange={handleCheckboxChange}
            />
            <label>
              {" "}
              I accept{" "}
              <span
                style={{
                  color: "#0055A8",
                  fontWeight: "bold",
                }}
              >
                Terms & Conditions
              </span>
            </label>
          </div>
          {termsError && (
            <p
              style={{
                color: "#CB2C2C",
                fontSize: "12px",
                marginLeft: "15px",
              }}
            >
              {termsError}
            </p>
          )}
        </Grid>
        <Grid item xs={12}>
          {isLoading ? (
            <LoadingSpinner />
          ) : (
            <AppButton
              title="Continue"
              fullWidth
              sx={{ mb: 2 }}
              onClick={handleContinue} // Use the new handler
              //disabled={!termsAccepted} // Disable button if terms are not accepted
            />
          )}
        </Grid>
      </Grid>
    </>
  );
};

export default SignUpDetail;
